// BatteryEngine.js
//
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — initial REST fetch
// (getDevices) plus live SignalR push (OnBatteryUpdated) for the device catalogue,
// same write path for both via BatteryCache.addOrUpdate (full replace per rk). Singleton — one instance
// shared across the app.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import batteryCache from './BatteryCache';
import batteryFetcher from './BatteryFetcher';
import batteryContextService from '@/services/BatteryContextService';

const _name = 'BatteryEngine';

class BatteryEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();

        this._unsub = this._wsNotifier.subscribe(events.OnBatteriesUpdated, this.onMessage);
        this._unsubDeleted = this._wsNotifier.subscribe(events.OnBatteriesDeleted, this.onDeleted);
    }

    clear() {
        super.clear();
        if (this._unsub) this._unsub();
        if (this._unsubDeleted) this._unsubDeleted();
    }

    /**
     * BE push — 1 device's catalogue fields per message, e.g. after adding/renaming
     * a battery. Assumed same shape as a getDevices() item — confirm with BE if not.
     * @param data array of battery context
     */
    onMessage = (data) => {
        try {
            batteryCache.addOrUpdate(data);
            this._notify(data.rk);
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * BE push — removed battery contexts.
     * @param {object[]} items - deleted contexts, only rk is used
     */
    onDeleted = (items) => {
        try {
            (items ?? []).forEach((item) => batteryCache.delete(item.rk));
            this._notify();
        } catch (error) {
            this.logError('onDeleted', error.message);
        }
    };

    /**
     * Initial fetch — full device catalogue via REST. Registered in
     * BootRegistry.bootFetchers, chained before BatStateEngine.fetch().
     * @returns {Promise<object[]>}
     */
    fetch = async () => {
        try {
            const items = await batteryFetcher.fetch();
            batteryCache.addOrUpdate(items);
            this._notify();
            return items;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Posts a new battery context, then refetches the catalogue.
     * @param {object} data - battery context DTO
     * @returns {Promise<boolean>} success
     */
    addBattery = async (data) => {
        try {
            await batteryContextService.postContexts([data]);
            await this.fetch();
            return true;
        } catch (error) {
            this.logError('addBattery', error.message);
            return false;
        }
    };

    /**
     * Posts an existing battery context (with rk = update), then refetches the catalogue.
     * @param {object} data - battery context DTO
     * @returns {Promise<boolean>} success
     */
    updateBattery = async (data) => {
        try {
            await batteryContextService.postContexts([data]);
            await this.fetch();
            return true;
        } catch (error) {
            this.logError('updateBattery', error.message);
            return false;
        }
    };

    /**
     * @returns {object[]} all cached battery rows
     */
    getAll = () => batteryCache.getAll();

    /**
     * @param {string} rk
     * @returns {object|null} cached row
     */
    getByRk = (rk) => batteryCache.get(rk);

    // notify draagt alleen de rk (of niets bij de initial batch-fetch) — component
    // leest altijd opnieuw uit batteryCache.get(rk), nooit uit het notify-payload.
    _notify = (rk) => {
        ComponentNotifier.notify(_name, 'dataChanged', { rk });
    };
}

const batteryEngine = new BatteryEngine();
export default batteryEngine;
